"use client";

/**
 * StorageMock — animated mock of the askFinz Storage workspace for /apps/storage.
 *
 * Pure presentation: file tree on the left, file detail + version history on
 * the right. A scripted sequence plays when the mock scrolls into view: a
 * file appears in the tree, gets versioned (badge ticks v1 → v2 → v3), then
 * a conflict surfaces on a different file with an inline keep-theirs /
 * keep-mine / merge resolver.
 *
 * NOT the real Storage UI (`storage.askfinz.ai/components/storage/*`).
 * Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Folder,
  FolderOpen,
  File as FileIcon,
  FileText,
  FileCode2,
  FileJson,
  FileImage,
  Search,
  UploadCloud,
  FolderPlus,
  History,
  RotateCcw,
  Download,
  AlertTriangle,
  Check,
  GitMerge,
  Clock,
} from "lucide-react";

type FileKind = "doc" | "code" | "json" | "image";

type FileEntry = {
  id: string;
  name: string;
  kind: FileKind;
  size: string;
  modified: string;
  version: number;
  active?: boolean;
};

type FolderEntry = {
  id: string;
  name: string;
  open: boolean;
  files: FileEntry[];
};

const INITIAL_TREE: FolderEntry[] = [
  {
    id: "f-research",
    name: "research",
    open: true,
    files: [
      { id: "fr-1", name: "fairness-frameworks.md",  kind: "doc",   size: "18.4 KB",  modified: "2 hours ago",  version: 7, active: true },
      { id: "fr-2", name: "interview-notes.md",      kind: "doc",   size: "6.1 KB",   modified: "yesterday",    version: 3 },
      { id: "fr-3", name: "citations.json",          kind: "json",  size: "44.2 KB",  modified: "3 days ago",   version: 12 },
    ],
  },
  {
    id: "f-config",
    name: "config",
    open: true,
    files: [
      { id: "fc-1", name: "auth.config.json", kind: "json", size: "2.4 KB", modified: "8 minutes ago", version: 4 },
    ],
  },
  {
    id: "f-assets",
    name: "assets",
    open: false,
    files: [
      { id: "fa-1", name: "diagram-loss.svg", kind: "image", size: "112 KB",  modified: "last week", version: 1 },
    ],
  },
];

type Version = {
  v: number;
  age: string;
  author: string;
  note: string;
  current?: boolean;
};

const VERSIONS: Version[] = [
  { v: 7, age: "2h ago",   author: "you",       note: "Reordered comparison; added NIST callout",   current: true },
  { v: 6, age: "yesterday", author: "Researcher · agent", note: "Synthesised 3 new sources" },
  { v: 5, age: "2 days",    author: "you",       note: "Cut speculative paragraph in §2" },
  { v: 4, age: "3 days",    author: "you",       note: "Initial outline + first draft" },
  { v: 3, age: "5 days",    author: "you",       note: "Stub" },
];

function iconFor(kind: FileKind) {
  if (kind === "doc")   return FileText;
  if (kind === "code")  return FileCode2;
  if (kind === "json")  return FileJson;
  if (kind === "image") return FileImage;
  return FileIcon;
}

type ConflictState = "idle" | "open" | "resolving" | "resolved";

export function StorageMock() {
  const [tree, setTree] = useState<FolderEntry[]>(INITIAL_TREE);
  const [activeVersion, setActiveVersion] = useState(7);
  const [conflict, setConflict] = useState<ConflictState>("idle");
  const [bumpFlash, setBumpFlash] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      // reset
      setTree(INITIAL_TREE);
      setActiveVersion(7);
      setConflict("idle");
      setBumpFlash(null);

      // 1. A new file appears in research/
      schedule(() => {
        setTree((prev) =>
          prev.map((f) =>
            f.id === "f-research"
              ? {
                  ...f,
                  files: [
                    ...f.files,
                    { id: "fr-new", name: "draft-v0.md", kind: "doc", size: "2.1 KB", modified: "just now", version: 1 },
                  ],
                }
              : f
          )
        );
      }, 600);

      // 2. Active file gets bumped v7 → v8 → v9 with a save flash
      schedule(() => {
        setBumpFlash(8);
        setTree((prev) =>
          prev.map((f) =>
            f.id === "f-research"
              ? { ...f, files: f.files.map((file) => (file.active ? { ...file, version: 8, modified: "just now" } : file)) }
              : f
          )
        );
        setActiveVersion(8);
      }, 1700);
      schedule(() => setBumpFlash(null), 2200);

      schedule(() => {
        setBumpFlash(9);
        setTree((prev) =>
          prev.map((f) =>
            f.id === "f-research"
              ? { ...f, files: f.files.map((file) => (file.active ? { ...file, version: 9 } : file)) }
              : f
          )
        );
        setActiveVersion(9);
      }, 2700);
      schedule(() => setBumpFlash(null), 3200);

      // 3. Conflict opens on auth.config.json
      schedule(() => setConflict("open"), 3900);

      // 4. User picks "merge"; resolves
      schedule(() => setConflict("resolving"), 5400);
      schedule(() => {
        setConflict("resolved");
        setTree((prev) =>
          prev.map((f) =>
            f.id === "f-config"
              ? { ...f, files: f.files.map((file) => ({ ...file, version: file.version + 1, modified: "just now" })) }
              : f
          )
        );
      }, 6200);

      // Loop
      schedule(runCycle, 9500);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  const versionsForView: Version[] = [
    { v: activeVersion, age: "just now", author: "you", note: "Tightened §3 conclusion", current: true },
    ...VERSIONS.filter((v) => v.v < activeVersion).map((v) => ({ ...v, current: false })),
  ].slice(0, 5);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            storage.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (conflict === "open" || conflict === "resolving"
                  ? "bg-[hsl(var(--accent))] animate-pulse"
                  : "bg-foreground/30")
              }
            />
            {conflict === "open" ? "Conflict" : conflict === "resolving" ? "Merging" : "Synced"}
          </span>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-3 py-2 border-b hairline bg-foreground/[0.015] overflow-x-auto">
          <div className="flex items-center gap-1.5 rounded-md border hairline bg-background px-2.5 py-1 text-[12px] text-foreground/65 min-w-[180px] shrink-0">
            <Search className="h-3 w-3" />
            <span className="opacity-70">Search files</span>
          </div>
          <div className="ml-auto flex items-center gap-1.5 shrink-0">
            <button type="button" className="inline-flex items-center gap-1.5 rounded-md border hairline px-2.5 py-1 text-[12px] text-foreground/75 hover:bg-foreground/5 transition">
              <FolderPlus className="h-3 w-3" /> New folder
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-md bg-foreground text-background px-2.5 py-1 text-[12px] hover:opacity-90 transition">
              <UploadCloud className="h-3 w-3" /> Upload
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 min-h-[440px] md:min-h-[480px]">
          {/* File tree */}
          <div className="col-span-12 md:col-span-5 border-r hairline bg-foreground/[0.02] p-2 text-[12px]">
            <div className="px-2 pb-2 mb-1 border-b hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              Files
            </div>
            <div className="space-y-0.5">
              {tree.map((f) => (
                <FolderRow
                  key={f.id}
                  folder={f}
                  bumpFlash={bumpFlash}
                  conflictFile={conflict === "open" || conflict === "resolving" ? "auth.config.json" : null}
                />
              ))}
            </div>

            {/* Conflict resolver */}
            {(conflict === "open" || conflict === "resolving") && (
              <div className="mt-3 mx-1 rounded-xl border hairline-strong bg-background p-3 animate-[blur-fade-in_0.5s_ease-out]">
                <div className="flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-[hsl(var(--accent))]">
                  <AlertTriangle className="h-3 w-3" />
                  Conflict
                </div>
                <p className="mt-1.5 text-[12px] text-foreground/80 leading-snug">
                  <span className="font-mono">auth.config.json</span> edited in two places.
                </p>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  <button type="button" className="rounded-md border hairline px-2 py-1 text-[11px] text-foreground/75 hover:bg-foreground/5 transition">
                    Keep theirs
                  </button>
                  <button type="button" className="rounded-md border hairline px-2 py-1 text-[11px] text-foreground/75 hover:bg-foreground/5 transition">
                    Keep mine
                  </button>
                  <button
                    type="button"
                    className={
                      "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] transition " +
                      (conflict === "resolving"
                        ? "bg-[hsl(var(--accent))] text-background animate-pulse"
                        : "bg-foreground text-background hover:opacity-90")
                    }
                  >
                    <GitMerge className="h-3 w-3" />
                    {conflict === "resolving" ? "Merging…" : "Merge"}
                  </button>
                </div>
              </div>
            )}

            {conflict === "resolved" && (
              <div className="mt-3 mx-1 rounded-xl border hairline bg-foreground/[0.03] p-3 text-[11px] text-foreground/65 inline-flex items-center gap-1.5 animate-[blur-fade-in_0.5s_ease-out]">
                <Check className="h-3 w-3 text-foreground/70" />
                Merged · saved as a new version.
              </div>
            )}
          </div>

          {/* File detail + version history */}
          <div className="col-span-12 md:col-span-7 flex flex-col bg-background">
            <div className="px-4 py-3 border-b hairline flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border hairline-strong bg-foreground/[0.03]">
                <FileText className="h-4 w-4 text-foreground/65" />
              </span>
              <div className="min-w-0">
                <div className="text-[13px] font-medium truncate">fairness-frameworks.md</div>
                <div className="text-[11px] text-foreground/65 font-mono truncate">/research/fairness-frameworks.md</div>
              </div>
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-md border hairline px-2 py-1 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                v{activeVersion}
              </span>
            </div>

            <div className="px-4 py-3 border-b hairline grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
              <div>
                <div className="uppercase tracking-[0.16em] text-foreground/65">Size</div>
                <div className="mt-1 text-foreground/85 font-mono">18.4 KB</div>
              </div>
              <div>
                <div className="uppercase tracking-[0.16em] text-foreground/65">Owner</div>
                <div className="mt-1 text-foreground/85">you</div>
              </div>
              <div>
                <div className="uppercase tracking-[0.16em] text-foreground/65">Shared</div>
                <div className="mt-1 text-foreground/85">2 collaborators</div>
              </div>
              <div>
                <div className="uppercase tracking-[0.16em] text-foreground/65">Hash</div>
                <div className="mt-1 text-foreground/85 font-mono truncate">sha · 8f02b1{(activeVersion).toString(16)}</div>
              </div>
            </div>

            <div className="px-4 py-3 flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <History className="h-3 w-3" />
              Version history
            </div>
            <div className="px-2 pb-4 flex-1 overflow-hidden">
              {versionsForView.map((v, i) => (
                <div
                  key={`${v.v}-${i}`}
                  className={
                    "flex items-start gap-3 mx-2 px-3 py-2.5 rounded-lg transition " +
                    (v.current ? "bg-foreground/[0.04] hairline border" : "hover:bg-foreground/[0.03]")
                  }
                >
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full border hairline text-[10px] font-mono text-foreground/70 shrink-0">
                    v{v.v}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-foreground/85 truncate">{v.note}</div>
                    <div className="mt-0.5 text-[10px] text-foreground/65 inline-flex items-center gap-1.5">
                      <Clock className="h-2.5 w-2.5" />
                      {v.age}
                      <span className="text-foreground/30">·</span>
                      {v.author}
                      {v.current && (
                        <span className="text-foreground/30">· <span className="text-foreground/70">current</span></span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-foreground/65 shrink-0">
                    <button type="button" className="p-1 rounded hover:bg-foreground/5 transition" aria-label="Restore">
                      <RotateCcw className="h-3 w-3" />
                    </button>
                    <button type="button" className="p-1 rounded hover:bg-foreground/5 transition" aria-label="Download">
                      <Download className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FolderRow({
  folder,
  bumpFlash,
  conflictFile,
}: {
  folder: FolderEntry;
  bumpFlash: number | null;
  conflictFile: string | null;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 py-1 px-2 rounded text-foreground/75">
        {folder.open ? <FolderOpen className="h-3.5 w-3.5 text-foreground/65" /> : <Folder className="h-3.5 w-3.5 text-foreground/65" />}
        <span>{folder.name}</span>
        <span className="ml-auto text-[10px] font-mono text-foreground/65">{folder.files.length}</span>
      </div>
      {folder.open && (
        <div className="ml-3">
          {folder.files.map((file) => {
            const Icon = iconFor(file.kind);
            const isConflict = conflictFile === file.name;
            const isNew = file.id === "fr-new";
            return (
              <div
                key={file.id}
                className={
                  "flex items-center gap-2 py-1.5 px-2 rounded transition " +
                  (file.active
                    ? "bg-foreground text-background"
                    : isConflict
                    ? "bg-[hsl(var(--accent))]/8 text-foreground/85"
                    : "hover:bg-foreground/5 text-foreground/70") +
                  (isNew ? " animate-[blur-fade-in_0.5s_ease-out]" : "")
                }
              >
                <Icon className={"h-3 w-3 shrink-0 " + (file.active ? "opacity-90" : "opacity-65")} />
                <span className="truncate font-mono text-[11px]">{file.name}</span>
                <span className={"ml-auto inline-flex items-center gap-1.5 text-[10px] " + (file.active ? "text-background/60" : "text-foreground/65")}>
                  <span className="font-mono">{file.size}</span>
                  <span
                    className={
                      "rounded px-1 py-px font-mono " +
                      (file.active
                        ? "bg-background/15 text-background/85"
                        : isConflict
                        ? "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]"
                        : "bg-foreground/[0.06] text-foreground/65") +
                      (bumpFlash && file.active ? " animate-pulse" : "")
                    }
                  >
                    v{file.version}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
